import { useCallback, useState } from 'react'
import { useRouter } from 'next/router'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'
import { useToast } from '@wartek-id/toast'

import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import EditDialog from './EditDialog'
import ConfirmDialog from './ConfirmDialog'
import { useAPIUpsertSKP } from 'api/kinerja/useAPIKinerja'
import {
  isValidPangkat,
  checkPangkatAndJenjang,
  getPangkatGolongan,
} from 'app/KinerjaPage/utils/pangkat'
import { isValidJenjang, mapJenjangName } from 'app/KinerjaPage/utils/jenjang'
import {
  DescriptionList,
  DescriptionListItem,
} from 'app/KinerjaShared/components/DescriptionList'
import { usePengelolaanKinerjaBaseUrl } from 'app/KinerjaShared/hooks/usePengelolaanKinerjaBaseUrl'
import useToggleSdkContext from 'utils/useToogleSdk'

import type {
  Jenjang,
  KinerjaProfil,
  KinerjaTeachingRoleType,
} from 'types/kinerja'

interface PanelProfilProps {
  profile: KinerjaProfil
  skipEditConfirmation?: boolean
  role?: KinerjaTeachingRoleType
}

export const WARNING_TEXT_PNS_PPPK = `Pastikan Jenjang Jabatan sesuai karena akan memengaruhi Perencanaan Kinerja. Saat ini data hanya bisa diedit satu kali saja, ya.`
export const WARNING_TEXT_NON_PNS_PPPK = `Silakan hubungi operator sekolah untuk pembaruan data diri Anda di Dapodik.`

interface ActionButtonProps {
  skipEditConfirmation: boolean
  isIncomplete: boolean
  isGuruStatusUndefined: boolean
  onConfirm: () => void
  onOpenEditDialog: (open: boolean) => void
  onOpenConfirmationDialog: (open: boolean) => void
}

function ActionButton({
  skipEditConfirmation,
  isIncomplete,
  isGuruStatusUndefined,
  onConfirm,
  onOpenEditDialog,
  onOpenConfirmationDialog,
}: ActionButtonProps) {
  if (skipEditConfirmation) {
    return (
      <Button size="lg" onClick={onConfirm}>
        Data sudah sesuai
      </Button>
    )
  }

  if (isIncomplete && !isGuruStatusUndefined) {
    return (
      <Button size="lg" color="black" onClick={() => onOpenEditDialog(true)}>
        Lengkapi data diri
      </Button>
    )
  }

  return (
    <>
      {!isGuruStatusUndefined && (
        <Button size="lg" color="white" onClick={() => onOpenEditDialog(true)}>
          Edit
        </Button>
      )}
      <Button size="lg" onClick={() => onOpenConfirmationDialog(true)}>
        Data sudah sesuai
      </Button>
    </>
  )
}

export default function PanelProfil({
  profile,
  skipEditConfirmation = false,
  role,
}: PanelProfilProps) {
  // create a boolean state to open dialog edit profile
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false)
  const [currentProfile, setCurrentProfile] = useState(profile)
  const [isModified, setIsModified] = useState(false)
  const toggles = useToggleSdkContext()

  const isSPNFEnabled = toggles?.['kinerja-spnf-enabled'] === true
  const isKinerjaPerencanaan2026Enabled =
    toggles?.['kinerja-perencanaan-2026-enabled'] === true

  const { mutateAsync: createNewSKP, isLoading } = useAPIUpsertSKP()

  const router = useRouter()
  const toast = useToast()

  const { periodYear } = usePeriodContext()

  // If the initial profile is not valid, (i.e. wrong pangkat or pangkat and jenjang does not match), we ask the user to modify it.
  // But we won't validate it again after the user modified it.

  const pangkatGolongan =
    isModified || isValidPangkat(currentProfile.pangkatGolongan)
      ? currentProfile.pangkatGolongan
      : '-'

  let jenjang = '-'

  if (isModified) {
    jenjang = currentProfile.jenjang
  } else if (currentProfile.kategoriPegawai === 'PNS') {
    jenjang = checkPangkatAndJenjang(
      currentProfile.pangkatGolongan,
      currentProfile.jenjang.toLowerCase() as Jenjang
    )
      ? currentProfile.jenjang
      : '-'
  } else if (currentProfile.kategoriPegawai === 'PPPK') {
    jenjang = isValidJenjang(currentProfile.jenjang)
      ? currentProfile.jenjang
      : '-'
  }

  let isIncomplete = false

  if (currentProfile.kategoriPegawai === 'PNS') {
    isIncomplete = pangkatGolongan === '-' || jenjang === '-'
  } else if (currentProfile.kategoriPegawai === 'PPPK') {
    isIncomplete = jenjang === '-'
  }

  const isGuruStatusUndefined = currentProfile.kategoriPegawai === 'UNDEFINED'

  const handleSave = (profile: KinerjaProfil) => {
    setIsModified(true)
    setCurrentProfile(profile)
  }

  const baseUrl = usePengelolaanKinerjaBaseUrl()

  const goToPerencanaanPage = useCallback(() => {
    if (
      role === 'GURU' &&
      periodYear >= 2026 &&
      isKinerjaPerencanaan2026Enabled
    ) {
      router.replace(`${baseUrl}/perencanaan/ajukan`)
    } else {
      router.replace(`${baseUrl}/perencanaan`)
    }
  }, [baseUrl, router, role, periodYear, isKinerjaPerencanaan2026Enabled])

  const handleConfirm = async () => {
    try {
      await createNewSKP({
        pangkatGolongan: currentProfile.pangkatGolongan,
        jenjang: currentProfile.jenjang,
        ...(isSPNFEnabled ? { role } : {}),
      })

      setIsConfirmDialogOpen(false)

      goToPerencanaanPage()
    } catch (error) {
      console.error(error)

      const message =
        error?.originalError?.response?.data?.error?.message ?? error.toString()

      toast({
        message: `Gagal mengubah profil: ${message}`,
      })
    }
  }

  return (
    <div className="p-9">
      <div className="rounded-lg bg-white mt-9 max-w-[1216px] m-auto">
        <div className="p-9 border-b">
          <Text variant="display-sm" className="mb-6" id="description-title">
            Cek data diri Anda
          </Text>

          {!skipEditConfirmation && (
            <div className="flex p-4 gap-3 items-center rounded bg-surface-warning-subdued mb-6">
              <Icon>information_outlined</Icon>
              <Text variant="body">
                {isGuruStatusUndefined
                  ? WARNING_TEXT_NON_PNS_PPPK
                  : WARNING_TEXT_PNS_PPPK}
              </Text>
            </div>
          )}

          <DescriptionList
            className="space-y-3"
            labelledById="description-title"
          >
            <DescriptionListItem label="Nama" value={currentProfile.nama} />

            {currentProfile.kategoriPegawai !== 'UNDEFINED' && (
              <DescriptionListItem
                label="NIP"
                value={currentProfile.nip || '-'}
              />
            )}

            <DescriptionListItem
              label="Status Kepegawaian"
              value={currentProfile.statusPegawai || '-'}
            />

            {currentProfile.kategoriPegawai === 'PNS' && (
              <DescriptionListItem
                label="Pangkat, Golongan Ruang"
                value={getPangkatGolongan(pangkatGolongan)}
              />
            )}

            {currentProfile.kategoriPegawai !== 'UNDEFINED' && (
              <DescriptionListItem
                label="Jenjang Jabatan"
                value={mapJenjangName(jenjang)}
              />
            )}

            <DescriptionListItem
              label="Satuan Pendidikan Induk"
              value={currentProfile.satuanPendidikan || '-'}
            />

            {currentProfile.timKinerja && (
              <DescriptionListItem
                label="Tim Kinerja"
                value={currentProfile.timKinerja || '-'}
              />
            )}

            {currentProfile.pejabatPenilaiKinerja && (
              <DescriptionListItem
                label="Pejabat Penilai Kinerja"
                value={currentProfile.pejabatPenilaiKinerja || '-'}
              />
            )}
          </DescriptionList>
        </div>

        <div className="flex py-4 px-7">
          <div className="ml-auto flex gap-5">
            <ActionButton
              skipEditConfirmation={skipEditConfirmation}
              isIncomplete={isIncomplete}
              isGuruStatusUndefined={isGuruStatusUndefined}
              onConfirm={handleConfirm}
              onOpenEditDialog={setIsDialogOpen}
              onOpenConfirmationDialog={setIsConfirmDialogOpen}
            />
          </div>
        </div>
      </div>

      {!skipEditConfirmation && (
        <>
          <EditDialog
            profile={currentProfile}
            isOpen={isDialogOpen}
            setOpen={setIsDialogOpen}
            onSave={handleSave}
          />
          <ConfirmDialog
            profile={currentProfile}
            isOpen={isConfirmDialogOpen}
            onClose={() => setIsConfirmDialogOpen(false)}
            onConfirm={handleConfirm}
            disabledConfirmButton={isLoading}
            isLoading={isLoading}
          />
        </>
      )}
    </div>
  )
}
